import {createRoutesFromElements, Navigate, Route} from 'react-router-dom';
import {ProtectedRoute} from '@features/auth';

import {
  ROUTE__BANNER_CREATE,
  ROUTE__BANNER_EDIT,
  ROUTE__BANNERS,
  ROUTE__BASKETBALL,
  ROUTE__CATEGORIES,
  ROUTE__CATEGORY_CREATE,
  ROUTE__CATEGORY_EDIT,
  ROUTE__CREATE_EVENT,
  ROUTE__CREATE_TOPIC,
  ROUTE__CREATE_VIDEO,
  ROUTE__CRYPTO,
  ROUTE__CRYPTO_CURRENCY_DETAILS,
  ROUTE__CURRENT_USER_PROFILE,
  ROUTE__DASHBOARD,
  ROUTE__DEPOSIT_FULL_INFO,
  ROUTE__EVENT_DETAILS,
  ROUTE__EVENT_LOGS,
  ROUTE__EVENT_ORDERS,
  ROUTE__EVENT_STAKES,
  ROUTE__EVENTS,
  ROUTE__GEO_FILTER_OPTION__CREATE,
  ROUTE__GEO_FILTER_OPTION__EDIT,
  ROUTE__GEO_FILTER_OPTIONS,
  ROUTE__KYC,
  ROUTE__LOGIN,
  ROUTE__LOGIN__ENTER_CODE,
  ROUTE__LOGIN__ENTER_PHONE,
  ROUTE__MARKETPLACE,
  ROUTE__MARKETPLACE_ITEM_CREATE,
  ROUTE__MARKETPLACE_ITEM_EDIT,
  ROUTE__ONBOARDING_DETAILS,
  ROUTE__ONBOARDINGS,
  ROUTE__ORDER_FULL_INFO,
  ROUTE__ORDERS,
  ROUTE__PARTICIPANT_CREATE,
  ROUTE__PARTICIPANT_DETAILS,
  ROUTE__PARTICIPANTS,
  ROUTE__PROMOCODE_CREATE,
  ROUTE__PROMOCODES,
  ROUTE__RATES,
  ROUTE__REFERRALS,
  ROUTE__REPORTS,
  ROUTE__SETTINGS_COUNTRIES,
  ROUTE__SETTINGS_PAYMENT_METHODS,
  ROUTE__SETTINGS_PAYMENT_METHODS__PAYOUTS,
  ROUTE__SETTINGS_VOUCHERS,
  ROUTE__SPORT_MATCHES,
  ROUTE__STAKES,
  ROUTE__SUB_CATEGORIES,
  ROUTE__SUB_CATEGORY_CREATE,
  ROUTE__SUB_CATEGORY_EDIT,
  ROUTE__TOPIC_CHAT,
  ROUTE__TOPIC_DETAILS,
  ROUTE__TOPIC_POOLS,
  ROUTE__TOPICS,
  ROUTE__TRANSACTION_FULL_INFO,
  ROUTE__TRANSACTIONS,
  ROUTE__TRANSACTIONS__DEPOSITS,
  ROUTE__TRANSACTIONS__POOL,
  ROUTE__TRANSACTIONS__WITHDRAWALS,
  ROUTE__USER_ACCOUNT,
  ROUTE__USER_ACCOUNT__BANS,
  ROUTE__USER_ACCOUNT__DEPOSITS,
  ROUTE__USER_ACCOUNT__ORDERS,
  ROUTE__USER_ACCOUNT__POOLS,
  ROUTE__USER_ACCOUNT__REFERRALS,
  ROUTE__USER_ACCOUNT__STAKES,
  ROUTE__USER_ACCOUNT__TRANSACTIONS,
  ROUTE__USER_ACCOUNT__TRANSACTIONS_POOL,
  ROUTE__USER_ACCOUNT__UTM,
  ROUTE__USER_ACCOUNT__WITHDRAWALS,
  ROUTE__USER_BANS,
  ROUTE__USERS,
  ROUTE__VIDEO_DETAILS,
  ROUTE__VIDEOS,
  ROUTE__WITHDRAWAL_FULL_INFO,
} from '@shared/constants';
import {AppWrapper} from './AppWrapper';

import {BannersPage} from '@/pages/banner/Banners';
import {CreateBannerPage} from '@/pages/banner/CreateBanner';
import {EditBannerPage} from '@/pages/banner/EditBanner';
import {BasketballLeaguesPage} from '@/pages/basketball/BasketballLeagues';
import {CategoriesPage} from '@/pages/category/Categories';
import {CreateCategoryPage} from '@/pages/category/CreateCategory';
import {CreateGeoFilterOptionPage} from '@/pages/category/CreateGeoFilterOption';
import {CreateSubCategoryPage} from '@/pages/category/CreateSubCategory';
import {EditCategoryPage} from '@/pages/category/EditCategory';
import {EditGeoFilterOptionPage} from '@/pages/category/EditGeoFilterOption';
import {EditSubCategoryPage} from '@/pages/category/EditSubCategory';
import {GeoCategoriesPage} from '@/pages/category/GeoCategories';
import {SubCategoriesPage} from '@/pages/category/SubCategories';
import {DashboardPage} from '@/pages/common/Dashboard';
import ErrorPage404 from '@/pages/common/Errors/404';
import {LoginPage} from '@/pages/common/LoginPage';
import {EnterCodeStep} from '@/pages/common/LoginPage/EnterCodeStep';
import {EnterPhoneStep} from '@/pages/common/LoginPage/EnterPhoneStep';
import {CurrenciesPage} from '@/pages/crypto/Currencies';
import {CurrencyDetailsPage} from '@/pages/crypto/CurrencyDetails';
import {CreateEventPage} from '@/pages/event/CreateEvent';
import {EditEventPage} from '@/pages/event/EditEvent';
import {EventsPage} from '@/pages/event/Events';
import {KycPage} from '@/pages/kyc/Kyc';
import {CreateMarketplaceItemPage} from '@/pages/marketplace/CreateMarketplaceItem';
import {EditMarketplaceItemPage} from '@/pages/marketplace/EditMarketplaceItem';
import {MarketplacePage} from '@/pages/marketplace/Marketplace';
import {OnboardingDetailsPage} from '@/pages/onboarding/OnboardingDetails';
import {OnboardingsPage} from '@/pages/onboarding/Onboardings';
import {OrderFullInfoPage} from '@/pages/order/OrderFullInfo';
import {OrdersPage} from '@/pages/order/Orders';
import {CreateParticipantPage} from '@/pages/participant/CreateParticipant';
import {EditParticipantPage} from '@/pages/participant/EditParticipant';
import {ParticipantsPage} from '@/pages/participant/Participants';
import {CreatePromocodePage} from '@/pages/promocode/CreatePromocode';
import {PromocodesPage} from '@/pages/promocode/Promocodes';
import {RatesPage} from '@/pages/rating/Rates';
import {ReferralsPage} from '@/pages/referral/Referrals';
import {ReportsPage} from '@/pages/report/Reports';
import {CountriesPage} from '@/pages/settings/Countries';
import {PaymentMethodsPage} from '@/pages/settings/PaymentMethods';
import {DepositMethodsTab} from '@/pages/settings/PaymentMethods/DepositMethodsTab';
import {PayoutMethodsTab} from '@/pages/settings/PaymentMethods/PayoutMethodsTab';
import {VoucherConfigsPage} from '@/pages/settings/VoucherConfigs';
import {SportMatchesPage} from '@/pages/sportMatch/SportMatches';
import {StakesPage} from '@/pages/stake/Stakes';
import {CreateTopicPage} from '@/pages/topic/CreateTopic';
import {EditTopicPage} from '@/pages/topic/EditTopic';
import {TopicsPage} from '@/pages/topic/Topics';
import {DepositFullInfoPage} from '@/pages/transaction/DepositFullInfo';
import {TransactionFullInfoPage} from '@/pages/transaction/TransactionFullInfo';
import {TransactionsPage} from '@/pages/transaction/Transactions';
import {AllDepositsTab} from '@/pages/transaction/Transactions/AllDepositsTab';
import {AllPoolTransactionsTab} from '@/pages/transaction/Transactions/AllPoolTransactionsTab';
import {AllTransactionsTab} from '@/pages/transaction/Transactions/AllTransactionsTab';
import {AllWithdrawalsTab} from '@/pages/transaction/Transactions/AllWithdrawalsTab';
import {WithdrawalFullInfoPage} from '@/pages/transaction/WithdrawalFullInfo';
import {BansPage} from '@/pages/user/Bans';
import {CurrentUserProfilePage} from '@/pages/user/CurrentUserProfilePage';
import {UserAccountPage} from '@/pages/user/UserAccount';
import {BansTab} from '@/pages/user/UserAccount/BansTab';
import {DepositsTab} from '@/pages/user/UserAccount/DepositsTab';
import {InformationTab} from '@/pages/user/UserAccount/InformationTab';
import {OrdersTab} from '@/pages/user/UserAccount/OrdersTab';
import {PoolsTab} from '@/pages/user/UserAccount/PoolsTab';
import {PoolTransactionsTab} from '@/pages/user/UserAccount/PoolTransactionsTab';
import {ReferralsTab} from '@/pages/user/UserAccount/ReferralsTab';
import {StakesTab} from '@/pages/user/UserAccount/StakesTab';
import {TransactionsTab} from '@/pages/user/UserAccount/TransactionsTab';
import {UtmTab} from '@/pages/user/UserAccount/UtmTab';
import {WithdrawalsTab} from '@/pages/user/UserAccount/WithdrawalsTab';
import {UsersPage} from '@/pages/user/Users';
import {CreateVideoPage} from '@/pages/video/CreateVideo';
import {VideoDetailsPage} from '@/pages/video/VideoDetails';
import {VideosPage} from '@/pages/video/Videos';

export const routes = createRoutesFromElements(
  <>
    <Route path="/" element={<AppWrapper />}>
      <Route path={ROUTE__LOGIN} element={<LoginPage />}>
        <Route path={ROUTE__LOGIN__ENTER_PHONE} element={<EnterPhoneStep />} />
        <Route path={ROUTE__LOGIN__ENTER_CODE} element={<EnterCodeStep />} />
        <Route
          index
          element={<Navigate replace to={ROUTE__LOGIN__ENTER_PHONE} />}
        />
        <Route path="*" element={<ErrorPage404 />} />
      </Route>

      <Route
        path={ROUTE__DASHBOARD}
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      >
        <Route path={ROUTE__USER_BANS} element={<BansPage />} />
        <Route path={ROUTE__USERS} element={<UsersPage />} />
        <Route path={ROUTE__USER_ACCOUNT} element={<UserAccountPage />}>
          <Route index element={<InformationTab />} />
          <Route path={ROUTE__USER_ACCOUNT__BANS} element={<BansTab />} />
          <Route
            path={ROUTE__USER_ACCOUNT__REFERRALS}
            element={<ReferralsTab />}
          />
          <Route path={ROUTE__USER_ACCOUNT__UTM} element={<UtmTab />} />
          <Route path={ROUTE__USER_ACCOUNT__ORDERS} element={<OrdersTab />} />
          <Route path={ROUTE__USER_ACCOUNT__STAKES} element={<StakesTab />} />
          <Route path={ROUTE__USER_ACCOUNT__POOLS} element={<PoolsTab />} />
          <Route
            path={ROUTE__USER_ACCOUNT__TRANSACTIONS}
            element={<TransactionsTab />}
          />
          <Route
            path={ROUTE__USER_ACCOUNT__TRANSACTIONS_POOL}
            element={<PoolTransactionsTab />}
          />
          <Route
            path={ROUTE__USER_ACCOUNT__DEPOSITS}
            element={<DepositsTab />}
          />
          <Route
            path={ROUTE__USER_ACCOUNT__WITHDRAWALS}
            element={<WithdrawalsTab />}
          />
        </Route>
        <Route path={ROUTE__ORDER_FULL_INFO} element={<OrderFullInfoPage />} />

        <Route path={ROUTE__EVENTS} element={<EventsPage />} />
        <Route path={ROUTE__EVENT_DETAILS} element={<EditEventPage />} />
        <Route path={ROUTE__EVENT_LOGS} element={<EditEventPage />} />
        <Route path={ROUTE__EVENT_ORDERS} element={<EditEventPage />} />
        <Route path={ROUTE__EVENT_STAKES} element={<EditEventPage />} />
        <Route path={ROUTE__CREATE_EVENT} element={<CreateEventPage />} />
        <Route path={ROUTE__SPORT_MATCHES} element={<SportMatchesPage />} />
        <Route path={ROUTE__BASKETBALL} element={<BasketballLeaguesPage />} />

        <Route path={ROUTE__TOPICS} element={<TopicsPage />} />
        <Route path={ROUTE__CREATE_TOPIC} element={<CreateTopicPage />} />
        <Route path={ROUTE__TOPIC_DETAILS} element={<EditTopicPage />} />
        <Route path={ROUTE__TOPIC_CHAT} element={<EditTopicPage />} />
        <Route path={ROUTE__TOPIC_POOLS} element={<EditTopicPage />} />

        <Route path={ROUTE__PARTICIPANTS} element={<ParticipantsPage />} />
        <Route
          path={ROUTE__PARTICIPANT_CREATE}
          element={<CreateParticipantPage />}
        />
        <Route
          path={ROUTE__PARTICIPANT_DETAILS}
          element={<EditParticipantPage />}
        />

        <Route path={ROUTE__KYC} element={<KycPage />} />
        <Route path={ROUTE__REPORTS} element={<ReportsPage />} />
        <Route path={ROUTE__REFERRALS} element={<ReferralsPage />} />

        <Route path={ROUTE__MARKETPLACE} element={<MarketplacePage />} />
        <Route
          path={ROUTE__MARKETPLACE_ITEM_CREATE}
          element={<CreateMarketplaceItemPage />}
        />
        <Route
          path={ROUTE__MARKETPLACE_ITEM_EDIT}
          element={<EditMarketplaceItemPage />}
        />

        <Route path={ROUTE__ORDERS} element={<OrdersPage />} />
        <Route path={ROUTE__STAKES} element={<StakesPage />} />

        <Route path={ROUTE__CATEGORIES} element={<CategoriesPage />} />
        <Route path={ROUTE__CATEGORY_CREATE} element={<CreateCategoryPage />} />
        <Route path={ROUTE__CATEGORY_EDIT} element={<EditCategoryPage />} />

        <Route path={ROUTE__SUB_CATEGORIES} element={<SubCategoriesPage />} />
        <Route
          path={ROUTE__SUB_CATEGORY_CREATE}
          element={<CreateSubCategoryPage />}
        />
        <Route
          path={ROUTE__SUB_CATEGORY_EDIT}
          element={<EditSubCategoryPage />}
        />

        <Route
          path={ROUTE__GEO_FILTER_OPTIONS}
          element={<GeoCategoriesPage />}
        />
        <Route
          path={ROUTE__GEO_FILTER_OPTION__CREATE}
          element={<CreateGeoFilterOptionPage />}
        />
        <Route
          path={ROUTE__GEO_FILTER_OPTION__EDIT}
          element={<EditGeoFilterOptionPage />}
        />

        <Route path={ROUTE__PROMOCODES} element={<PromocodesPage />} />
        <Route
          path={ROUTE__PROMOCODE_CREATE}
          element={<CreatePromocodePage />}
        />

        <Route path={ROUTE__TRANSACTIONS} element={<TransactionsPage />}>
          <Route index element={<AllTransactionsTab />} />
          <Route
            path={ROUTE__TRANSACTIONS__POOL}
            element={<AllPoolTransactionsTab />}
          />
          <Route
            path={ROUTE__TRANSACTIONS__DEPOSITS}
            element={<AllDepositsTab />}
          />
          <Route
            path={ROUTE__TRANSACTIONS__WITHDRAWALS}
            element={<AllWithdrawalsTab />}
          />
        </Route>

        <Route
          path={ROUTE__TRANSACTION_FULL_INFO}
          element={<TransactionFullInfoPage />}
        />
        <Route
          path={ROUTE__DEPOSIT_FULL_INFO}
          element={<DepositFullInfoPage />}
        />
        <Route
          path={ROUTE__WITHDRAWAL_FULL_INFO}
          element={<WithdrawalFullInfoPage />}
        />

        <Route path={ROUTE__RATES} element={<RatesPage />} />

        <Route path={ROUTE__BANNERS} element={<BannersPage />} />
        <Route path={ROUTE__BANNER_CREATE} element={<CreateBannerPage />} />
        <Route path={ROUTE__BANNER_EDIT} element={<EditBannerPage />} />

        <Route path={ROUTE__SETTINGS_COUNTRIES} element={<CountriesPage />} />
        <Route
          path={ROUTE__SETTINGS_VOUCHERS}
          element={<VoucherConfigsPage />}
        />

        <Route
          path={ROUTE__SETTINGS_PAYMENT_METHODS}
          element={<PaymentMethodsPage />}
        >
          <Route index element={<DepositMethodsTab />} />
          <Route
            path={ROUTE__SETTINGS_PAYMENT_METHODS__PAYOUTS}
            element={<PayoutMethodsTab />}
          />
        </Route>

        <Route path={ROUTE__VIDEOS} element={<VideosPage />} />
        <Route path={ROUTE__CREATE_VIDEO} element={<CreateVideoPage />} />
        <Route path={ROUTE__VIDEO_DETAILS} element={<VideoDetailsPage />} />
        <Route path={ROUTE__CRYPTO} element={<CurrenciesPage />} />
        <Route
          path={ROUTE__CRYPTO_CURRENCY_DETAILS}
          element={<CurrencyDetailsPage />}
        />

        <Route path={ROUTE__ONBOARDINGS} element={<OnboardingsPage />} />
        <Route
          path={ROUTE__ONBOARDING_DETAILS}
          element={<OnboardingDetailsPage />}
        />

        <Route index element={<Navigate replace to={ROUTE__USERS} />} />
        <Route path="*" element={<ErrorPage404 />} />
      </Route>

      <Route
        path={ROUTE__CURRENT_USER_PROFILE}
        element={
          <ProtectedRoute>
            <CurrentUserProfilePage />
          </ProtectedRoute>
        }
      />

      <Route path="/" element={<Navigate replace to={ROUTE__DASHBOARD} />} />

      <Route path="*" element={<ErrorPage404 />} />
    </Route>
  </>,
);
